'use client'

import { createTheme, ThemeProvider } from '@mui/material/styles'
import { FC, PropsWithChildren } from 'react'
import { theme as baseTheme } from '~/app/theme'

// Colors derived from (home)2 light mode CSS variables:
// --background: #ffffff  --foreground: #171717
// --card: #fafafa  --card-foreground: #171717
// --primary: hsl(186 85% 35%) ≈ #0e7490
// --muted: #eaebeb  --muted-foreground: #555859
// --border: #e8e8e8  --input: #bfbfbf
// --destructive: #c51616

const homeTheme = createTheme(baseTheme, {
  palette: {
    mode: 'light',

    primary: {
      light: '#22d3ee',
      main: '#0e7490',
      dark: '#155e75',
    },

    secondary: {
      light: '#dfe0e1',
      main: '#555859',
      dark: '#374151',
    },

    background: {
      default: '#ffffff',
      paper: '#fafafa',
    },

    text: {
      primary: '#171717',
      secondary: '#555859',
      disabled: '#9ca3af',
    },

    divider: '#e8e8e8',

    action: {
      disabled: '#9ca3af',
      disabledBackground: '#e5e7eb',
      hover: 'rgba(14, 116, 144, 0.06)',
    },

    success: {
      main: '#059669',
    },

    error: {
      main: '#c51616',
    },
  },

  components: {
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#fafafa',
          borderRadius: 12,
          border: 'none',
          boxShadow:
            '0 1px 2px -1px rgba(0, 0, 0, 0.08), 0 4px 6px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e8e8e8',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#fafafa',
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'transparent',
          '&:before': {
            display: 'none',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          color: '#171717',
          borderBottom: '1px solid #e8e8e8',
          '&.MuiTableCell-body, &.MuiTableCell-head': {
            border: 'none',
            borderBottom: '1px solid #e8e8e8',
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',

          '&.MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            border: '1px solid #d1d5db',
            color: '#171717',
            borderRadius: 8,

            input: {
              padding: '10px 12px',
              color: '#171717',
              fontSize: '0.875rem',

              '&::placeholder': {
                color: '#9ca3af',
                opacity: 1,
              },
            },

            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },

            '&:hover': {
              backgroundColor: '#ffffff',
              borderColor: '#9ca3af',
            },

            '&.Mui-focused': {
              backgroundColor: '#ffffff',
              borderColor: '#0e7490',
            },
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#6b7280',
          fontSize: '0.875rem',
          '&.Mui-focused': {
            color: '#0e7490',
          },
          '&.MuiInputLabel-shrink': {
            backgroundColor: '#ffffff',
            borderRadius: '4px',
            padding: '0 4px',
          },
        },
        outlined: {
          '&.MuiInputLabel-shrink': {
            backgroundColor: '#ffffff',
            borderRadius: '4px',
            padding: '0 4px',
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          color: '#171717',
        },
        icon: {
          color: '#6b7280',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#171717',
          fontSize: '0.875rem',
          '&:hover': {
            backgroundColor: 'rgba(14, 116, 144, 0.06)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(14, 116, 144, 0.1)',
          },
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          border: '1px solid #e8e8e8',
          borderRadius: 12,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          backgroundImage: 'none',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: '#0e7490',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#155e75',
            color: '#ffffff',
          },
        },
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          color: '#0e7490',
          textDecoration: 'none',
          '&:hover': {
            color: '#155e75',
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#e8e8e8',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        standardError: {
          backgroundColor: 'rgba(197, 22, 22, 0.08)',
          color: '#c51616',
        },
        standardSuccess: {
          backgroundColor: 'rgba(5, 150, 105, 0.08)',
          color: '#059669',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid #e8e8e8',
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:not(.MuiInputBase-multiline)': {
            height: 42,
          },
          '& input[type="date"]': {
            colorScheme: 'light',
          },
        },
      },
    },
  },
})

const HomeTheme: FC<PropsWithChildren> = ({ children }) => {
  return <ThemeProvider theme={homeTheme}>{children}</ThemeProvider>
}

export default HomeTheme
